import { RouteConfig } from 'vue-router';
import { NAME_ROUTES } from '../core/utils/name-routes';
import { RouteAccess, RouteKey, SessionConfig, TenantApi } from '../tenants/types';

/** Dónde se monta una ruta: suelta (pública) o como hija del layout principal (`HomePage`). */
export enum RouteLayout {
    Public = 'public',
    Shell = 'shell'
}

/** Nombre del parámetro de ruta que trae el token en las rutas `RouteAccess.UrlToken`. */
export const URL_TOKEN_PARAM = 'token';

export interface RouteDefinition {
    layout: RouteLayout;
    /** Acceso fijo de la ruta (no depende del tenant). Viaja en `meta.access` para el guard. */
    access: RouteAccess;
    route: RouteConfig;
    /** Puertos opcionales de `TenantApi` que la página necesita: si el tenant no los tiene, falla al arrancar. */
    requiredPorts?: Array<keyof TenantApi>;
}

/**
 * `meta` de una ruta: `access` es lo que lee `AccessCoordinator` (siempre el de la ruta destino, no el heredado);
 * `requiresAuth` queda como información (solo las rutas de sesión lo tienen en `true`).
 */
export const routeMeta = (access: RouteAccess) => ({ access, requiresAuth: access === RouteAccess.Session });

const toRouteConfig = (definition: RouteDefinition): RouteConfig => ({
    ...definition.route,
    meta: { ...(definition.route.meta || {}), ...routeMeta(definition.access) }
});

/**
 * Registro de todas las rutas conocidas. Cada tenant habilita un subconjunto con `routes: RouteKey[]`.
 * Es un `Record<RouteKey, ...>`: si se agrega un `RouteKey` sin su definición, falla al compilar.
 * Los paths y nombres son los de siempre (`NAME_ROUTES`) para no romper links existentes. Cada ruta tiene un solo
 * path y un acceso fijo (`access`), que `buildRoutes` copia a `meta`.
 */
export const ROUTE_REGISTRY: Record<RouteKey, RouteDefinition> = {
    [RouteKey.Login]: {
        layout: RouteLayout.Public,
        access: RouteAccess.Public,
        route: {
            path: NAME_ROUTES.LOGIN.path,
            name: NAME_ROUTES.LOGIN.name,
            component: () => import('../features/auth/views/LoginPage.vue')
        }
    },
    [RouteKey.DeleteAccount]: {
        layout: RouteLayout.Public,
        access: RouteAccess.Public,
        route: {
            path: NAME_ROUTES.DELETE_ACCOUNT.path,
            name: NAME_ROUTES.DELETE_ACCOUNT.name,
            component: () => import('../features/auth/views/DeleteAccountPage.vue')
        }
    },
    [RouteKey.Item]: {
        layout: RouteLayout.Shell,
        access: RouteAccess.UrlToken,
        route: {
            path: NAME_ROUTES.ITEM_DETAIL.path,
            component: () => import('../features/item/PageItemDetail.vue'),
            name: NAME_ROUTES.ITEM_DETAIL.name,
            props: route => ({
                id: route.params.id,
                token: route.params[URL_TOKEN_PARAM]
            })
        }
    },
    [RouteKey.ItemClaims]: {
        layout: RouteLayout.Shell,
        access: RouteAccess.UrlToken,
        requiredPorts: ['itemClaims'],
        route: {
            path: NAME_ROUTES.ITEM_CLAIMS.path,
            component: () => import('../features/item-claims/PageItemClaims.vue'),
            name: NAME_ROUTES.ITEM_CLAIMS.name,
            props: route => ({
                id: route.params.id,
                token: route.params[URL_TOKEN_PARAM]
            })
        }
    },
    [RouteKey.ClaimsList]: {
        layout: RouteLayout.Shell,
        access: RouteAccess.Session,
        requiredPorts: ['claimManagement'],
        route: {
            path: NAME_ROUTES.CLAIMS_LIST.path,
            component: () => import('../features/claims-list/PageClaimsList.vue'),
            name: NAME_ROUTES.CLAIMS_LIST.name
        }
    },
    [RouteKey.ClaimDetail]: {
        layout: RouteLayout.Shell,
        access: RouteAccess.Session,
        requiredPorts: ['claimManagement'],
        route: {
            path: NAME_ROUTES.CLAIM_DETAIL.path,
            component: () => import('../features/claim-detail/PageClaimDetail.vue'),
            name: NAME_ROUTES.CLAIM_DETAIL.name,
            props: route => ({
                id: route.params.id
            })
        }
    }
};


/**
 * Layout principal (`HomePage` + `HeaderToolbar`) para las rutas `RouteLayout.Shell` de un mismo acceso.
 * Solo el shell de `RouteAccess.Session` lleva `requiresAuth`; el de `RouteAccess.UrlToken` no.
 * `meta.access` del shell decide qué pasa al entrar a `/` (Session: al login o al landing; UrlToken: nada).
 */
export const buildShellRoute = (access: RouteAccess, children: RouteConfig[]): RouteConfig => ({
    path: NAME_ROUTES.HOME.path,
    component: () => import('../core/layout/views/HomePage.vue'),
    meta: routeMeta(access),
    children
});

export const NOT_FOUND_ROUTE: RouteConfig = {
    path: '*',
    name: 'Not Found',
    component: () => import('../core/layout/views/PageNotFound.vue'),
    meta: routeMeta(RouteAccess.Public)
};

/**
 * Orden de los shells. El primero se queda con `/`: vue-router ignora un segundo record con el mismo path, pero
 * registra igual sus hijas (paths absolutos). Con sesión, `/` sigue llevando al login o al landing.
 */
const SHELL_ACCESS_ORDER: RouteAccess[] = [RouteAccess.Session, RouteAccess.UrlToken];

/** Arma la tabla de rutas de vue-router a partir de las claves habilitadas por el tenant (respeta su orden). */
export const buildRoutes = (keys: RouteKey[]): RouteConfig[] => {
    const definitions = keys.map(key => ROUTE_REGISTRY[key]);
    const publicRoutes = definitions.filter(d => d.layout === RouteLayout.Public).map(toRouteConfig);
    const shellRoutes = SHELL_ACCESS_ORDER.map(access => ({
        access,
        children: definitions.filter(d => d.layout === RouteLayout.Shell && d.access === access).map(toRouteConfig)
    }))
        .filter(group => group.children.length)
        .map(group => buildShellRoute(group.access, group.children));

    return [...publicRoutes, ...shellRoutes, NOT_FOUND_ROUTE];
};

export const routePath = (key: RouteKey): string => ROUTE_REGISTRY[key].route.path;

/** Accesos que usan las rutas habilitadas (sin repetir). */
export const accessesForRoutes = (keys: RouteKey[]): RouteAccess[] =>
    keys.map(key => ROUTE_REGISTRY[key].access).filter((access, index, all) => all.indexOf(access) === index);

/** Puertos opcionales que faltan en el adaptador para las rutas habilitadas (vacío = OK). */
export const missingPortsForRoutes = (keys: RouteKey[], api: TenantApi): Array<keyof TenantApi> =>
    keys
        .map(key => ROUTE_REGISTRY[key].requiredPorts || [])
        .reduce((acc, ports) => acc.concat(ports), [] as Array<keyof TenantApi>)
        .filter((port, index, all) => all.indexOf(port) === index && !api[port]);

/**
 * Errores de configuración de acceso (vacío = OK). Mismo criterio que `missingPortsForRoutes`: la app falla al
 * arrancar en vez de fallar al navegar.
 * - una ruta `RouteAccess.Session` exige `session`;
 * - `session.loginRoute` y `session.landingRoute` tienen que estar habilitadas, y el login tiene que ser público.
 */
export const accessConfigErrors = (keys: RouteKey[], session: SessionConfig | undefined): string[] => {
    const errors: string[] = [];
    const sessionRoutes = keys.filter(key => ROUTE_REGISTRY[key].access === RouteAccess.Session);
    if (sessionRoutes.length && !session) {
        errors.push(`las rutas ${sessionRoutes.join(', ')} son de sesión y el tenant no define \`session\``);
    }
    if (session) {
        if (!keys.includes(session.loginRoute)) {
            errors.push(`\`session.loginRoute\` (${session.loginRoute}) no está en \`routes\``);
        } else if (ROUTE_REGISTRY[session.loginRoute].access !== RouteAccess.Public) {
            errors.push(`\`session.loginRoute\` (${session.loginRoute}) tiene que ser una ruta pública`);
        }
        if (!keys.includes(session.landingRoute)) {
            errors.push(`\`session.landingRoute\` (${session.landingRoute}) no está en \`routes\``);
        }
    }
    return errors;
};

export const ROOT_PATH = NAME_ROUTES.HOME.path;
